import type { UserInfo } from '@/typings/types';

export const backendToUserInfoMapping: {
  [backendKey: string]: keyof UserInfo;
} = {
  // Direct mappings
  uuid: 'uuid',
  avatar: 'avatar',
  name: 'name',
  username: 'username',
  email: 'email',
  phone: 'phone',

  // Snake case to camel case mappings
  is_guest: 'isGuest',
  is_authenticated: 'isAuthenticated',
  user_agent: 'userAgent',
  browser_digest: 'browserDigest',
  api_tier: 'apiTier',
  data_usage_consent: 'dataUsageConsent',
  custom_agents: 'customAgents',
  half_space_unicode: 'halfSpaceUnicode',
  custom_agents_costs: 'customAgentsCosts',
  guides: 'guides',
  total_unit: 'totalUnit',
  use_general_system_message: 'useGeneralSystemMessage',
  general_system_message: 'generalSystemMessage',
  referral_code: 'referralCode',

  // Special subscription fields
  'subscription.subscription_type': 'tier',
  'subscription.start_date': 'subscriptionStartDate',
  'subscription.end_date': 'subscriptionEndDate',
  subscription_end_date: 'subscriptionEndDate',
  subscription_start_date: 'subscriptionStartDate',
  user_tier: 'tier',
  user_api_tier: 'apiTier',
  exchange_rate: 'currencyExchangeRate',
  total_paid_in_unit: 'totalPaidInUnit',
  org_projects_enabled: 'orgProjectsEnabled'
};

const USER_TIERS: ReadonlySet<UserInfo['tier']> = new Set(['guest', 'free', 'tier1', 'tier2', 'tier3']);

const USER_API_TIERS: ReadonlySet<UserInfo['apiTier']> = new Set([
  'zero',
  'tier0',
  'tier1',
  'tier2',
  'tier3',
  'tier4',
  'tier5'
]);

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object') return false;
  return Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null;
}

function isRecordOfFiniteNumbers(value: unknown): value is Record<string, number> {
  if (!isPlainObject(value)) return false;

  return Object.values(value).every(item => typeof item === 'number' && Number.isFinite(item));
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function readNullableString(value: unknown): string | null | undefined {
  if (value === null) return null;
  return readString(value);
}

function readBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function readFiniteNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function readUserTier(value: unknown): UserInfo['tier'] | undefined {
  return typeof value === 'string' && USER_TIERS.has(value as UserInfo['tier'])
    ? (value as UserInfo['tier'])
    : undefined;
}

function readUserApiTier(value: unknown): UserInfo['apiTier'] | undefined {
  return typeof value === 'string' && USER_API_TIERS.has(value as UserInfo['apiTier'])
    ? (value as UserInfo['apiTier'])
    : undefined;
}

function readArray(value: unknown): unknown[] | undefined {
  return Array.isArray(value) ? value : undefined;
}

type UserInfoFieldReader = (value: unknown) => unknown;

const USER_INFO_FIELD_READERS: Partial<Record<keyof UserInfo, UserInfoFieldReader>> = {
  uuid: readString,
  avatar: readString,
  name: readString,
  username: readString,
  email: readString,
  phone: readString,
  userAgent: readNullableString,
  browserDigest: readNullableString,
  tier: readUserTier,
  apiTier: readUserApiTier,
  dataUsageConsent: readBoolean,
  customAgents: readArray,
  halfSpaceUnicode: readBoolean,
  customAgentsCosts: value => (isRecordOfFiniteNumbers(value) ? value : undefined),
  guides: readBoolean,
  totalUnit: readFiniteNumber,
  useGeneralSystemMessage: readBoolean,
  generalSystemMessage: readString,
  referralCode: readString,
  subscriptionStartDate: readString,
  subscriptionEndDate: readNullableString,
  currencyExchangeRate: readFiniteNumber,
  totalPaidInUnit: readFiniteNumber,
  isGuest: readBoolean,
  isAuthenticated: readBoolean,
  orgProjectsEnabled: readBoolean
};

function toSafeUserInfoValue(userInfoKey: keyof UserInfo, value: unknown): unknown {
  if (value === undefined) return undefined;

  return USER_INFO_FIELD_READERS[userInfoKey]?.(value);
}

export function buildUserInfoUpdatesFromResponse(response: unknown): Partial<UserInfo> {
  if (!isPlainObject(response)) return {};

  const updates: Partial<Record<keyof UserInfo, unknown>> = { orgProjectsEnabled: response.org_projects_enabled === true };

  Object.entries(response).forEach(([backendKey, value]) => {
    if (backendKey === 'username' && typeof value === 'string' && value.length > 0) {
      updates.username = value;
      updates.isAuthenticated = true;
      return;
    }

    const userInfoKey = backendToUserInfoMapping[backendKey];
    if (!userInfoKey) return;

    const safeValue = toSafeUserInfoValue(userInfoKey, value);
    if (safeValue !== undefined) {
      updates[userInfoKey] = safeValue;
    }
  });

  return updates as Partial<UserInfo>;
}
